import { FormWarning } from "@shltelemedicine/shl-ui-lib";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import { FormattedMessage, useIntl } from "react-intl";
import {
  DEFAULT_PHONE_COUNTRY_CODE,
  emailPattern,
  numberPattern,
} from "../../../../constants";
import { useAuthContext } from "../../../../context/AuthContext";
import { useManageUsersContext } from "../../../../context/ManageUsersContext";
import useLoadDomains from "../../../../hooks/useLoadDomains";
import { UserDetailsFormInputs } from "../../../../models/users.interface";
import { isEmailDomainsExist } from "../../../../utils/userUtil";
import ConnectForm from "../../../Shared/ConnectForm/ConnectForm";
import EntitySelectFormInput from "../../../Shared/EntitySelectFormInput/EntitySelectFormInput";
import SelectFormInput from "../../../Shared/SelectInput/SelectFormInput";
import TextFormInput from "../../../Shared/TextInput/TextFormInput";
import TitleSelectFormInput from "../TitleSelectFormInput/TitleSelectFormInput";
import styles from "./UserInfoInputs.module.css";
import CheckBoxFormInput from "../../../Shared/CheckBox/CheckBoxFormInput";
import CountryCodeSelect from "../../../Shared/CountryCodeSelect/CountryCodeSelect";
import { Controller } from "react-hook-form";
import { useEffect, useState } from "react";
import { getPhoneNumberLengthByCountryCode } from "../../../../utils/phoneUtil";
import EntitySelectTreeView from "../../../Shared/EntitySelectTreeView/EntitySelectTreeView";

type UserInfoInputsProps = {
  isEdit?: boolean;
  disabled?: boolean;
  entity?: {
    readonly?: boolean;
    restricted?: "pro" | "non-pro";
  };
  isModal?: boolean;
};

const UserInfoInputs = ({
  isEdit,
  entity,
  disabled,
  isModal = false,
}: UserInfoInputsProps) => {
  const intl = useIntl();

  const entityRequired = intl.messages[
    "MANAGEMENT.ADD_USER.ENTITY_REQUIRED"
  ] as string;

  const { domains, domainPlaceholder, userDomain } = useLoadDomains();

  const {
    state: { user },
  } = useAuthContext();
  const {
    state: {
      selectedUser,
      userOrgPermission: { loading },
    },
  } = useManageUsersContext();

  const isCurrentUser = user?.email === selectedUser?.email;
  const showEmailDomains = isEmailDomainsExist(domains);
  const defaultPhoneCode =
    selectedUser?.countryCode || DEFAULT_PHONE_COUNTRY_CODE;
  const [requiredPhoneLength, setRequiredPhoneLength] = useState(0);
  useEffect(() => {
    setRequiredPhoneLength(getPhoneNumberLengthByCountryCode(defaultPhoneCode));
  }, [defaultPhoneCode]);

  const getDomains = () => {
    if (!(showEmailDomains && isEdit && userDomain)) return domains;
    return domains.find((i) => i.value === userDomain.value)
      ? domains
      : [userDomain, ...domains];
  };

  return (
    <ConnectForm<UserDetailsFormInputs>>
      {({ control, formState: { isSubmitting, errors: formErrors } }) => {
        const errorPlaceholder =
          formErrors?.firstName || formErrors?.lastName ? (
            <p
              className={`mb-2 text-center font-sm flex-grow-1`}
              style={{
                color: "white",
                padding: "0.25rem",
                visibility: "hidden",
              }}
            >
              Placeholder
            </p>
          ) : (
            <></>
          );

        return (
          <div className={styles.wrapper}>
            <Form.Group className="mb-2">
              <Form.Label className="mb-1 font-md d-flex align-items-center">
                <span className="prim-color">*</span>
                &nbsp;
                <FormattedMessage
                  id="MANAGEMENT.ENTITY"
                  defaultMessage="Entity"
                />
                :<span className="mx-1"></span>
                {!!formErrors?.entity && (
                  <FormWarning
                    className="font-sm flex-grow-1"
                    message={entityRequired}
                  />
                )}
              </Form.Label>

              <Controller
                name="entity"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <EntitySelectTreeView
                    disabled={
                      entity?.readonly || isSubmitting || loading || disabled
                    }
                    setValueOnChange={field.onChange}
                    selectedOrgId={field.value ? +field.value : undefined}
                    selectedUserId={selectedUser?.id}
                  />
                )}
              />
            </Form.Group>
            <Row className="mb-2 align-items-end">
              <Form.Group as={Col} className="mb-0">
                <Form.Label className="mb-1 font-md d-flex align-items-center">
                  <span className="prim-color">*</span>
                  &nbsp;
                  <FormattedMessage
                    id="USER.FIRST_NAME"
                    defaultMessage="First Name"
                  />
                  :
                </Form.Label>

                <TextFormInput<UserDetailsFormInputs>
                  name={"firstName"}
                  control={control}
                  rules={{
                    required: true,
                  }}
                  input={{
                    isInvalid: !!formErrors?.firstName,
                    disabled: isSubmitting || loading || disabled,
                    readOnly: isSubmitting || loading || disabled,
                  }}
                />
                {!!formErrors?.firstName ? (
                  <FormWarning
                    className="font-sm flex-grow-1 mt-2"
                    message={
                      <FormattedMessage
                        id="MANAGEMENT.ADD_USER.FIRST_NAME_REQUIRED"
                        defaultMessage="Insert First name"
                      />
                    }
                  />
                ) : (
                  errorPlaceholder
                )}
              </Form.Group>
              <Form.Group as={Col} className="mb-0 px-0">
                <Form.Label className="mb-1 font-md">
                  <span className="prim-color">*</span>
                  &nbsp;
                  <FormattedMessage
                    id="USER.LAST_NAME"
                    defaultMessage="Last Name"
                  />
                  :
                </Form.Label>

                <TextFormInput<UserDetailsFormInputs>
                  name={"lastName"}
                  control={control}
                  rules={{
                    required: true,
                  }}
                  input={{
                    isInvalid: !!formErrors?.lastName,
                    disabled: isSubmitting || loading || disabled,
                    readOnly: isSubmitting || loading || disabled,
                  }}
                />
                {!!formErrors?.lastName ? (
                  <FormWarning
                    className="font-sm flex-grow-1 mt-2"
                    message={
                      <FormattedMessage
                        id="MANAGEMENT.ADD_USER.LAST_NAME_REQUIRED"
                        defaultMessage="Insert Last name"
                      />
                    }
                  />
                ) : (
                  errorPlaceholder
                )}
              </Form.Group>
              <Form.Group as={Col} className="mb-0 col-3">
                <Form.Label className="mb-1 font-md">
                  <FormattedMessage id="USER.TITLE" defaultMessage="Title" />:
                </Form.Label>

                <TitleSelectFormInput
                  control={control}
                  disabled={isSubmitting || loading || disabled}
                  isInvalid={!!formErrors?.title}
                />
                {errorPlaceholder}
              </Form.Group>
            </Row>
            <Row className="mb-2">
              {/* <Row style={{ alignItems: "center" }}> */}
              <Form.Group as={Col} className="pl-3 px-0">
                <Form.Label className="mb-1 font-md">
                  <span className="prim-color">*</span>
                  &nbsp;
                  <FormattedMessage
                    id={`${
                      showEmailDomains
                        ? "USER.EMAIL_WITH_RESTRICTED_DOMAIN"
                        : "USER.EMAIL"
                    }`}
                    defaultMessage={`${
                      showEmailDomains ? "Login Name [Email]" : "Email"
                    }`}
                  />
                  :
                </Form.Label>
                <div className="d-flex align-items-center">
                  <div className={"flex-grow-1 d-flex"}>
                    <TextFormInput<UserDetailsFormInputs>
                      name={showEmailDomains ? "loginName" : "email"}
                      control={control}
                      rules={{
                        required: true,
                        pattern: showEmailDomains ? undefined : emailPattern,
                      }}
                      input={{
                        isInvalid: showEmailDomains
                          ? !!formErrors?.loginName
                          : !!formErrors?.email,
                        readOnly:
                          isSubmitting ||
                          (isCurrentUser && !showEmailDomains && isEdit) ||
                          loading ||
                          disabled,
                        disabled:
                          isSubmitting ||
                          (isCurrentUser && !showEmailDomains && isEdit) ||
                          loading ||
                          disabled,
                      }}
                    />
                  </div>

                  {showEmailDomains && (
                    <div className={"flex-grow-1 d-flex align-items-center"}>
                      &nbsp;@&nbsp;
                      <SelectFormInput<UserDetailsFormInputs>
                        name={"emailDomain"}
                        control={control}
                        rules={{
                          required: true,
                        }}
                        input={{
                          items: getDomains(),
                          disabled:
                            isCurrentUser ||
                            isSubmitting ||
                            loading ||
                            disabled,
                          readOnly:
                            isCurrentUser ||
                            isSubmitting ||
                            loading ||
                            disabled,
                          isInvalid: !!formErrors?.emailDomain,
                          placeholder: domainPlaceholder,
                        }}
                      />
                    </div>
                  )}
                </div>
                {(!!formErrors?.email || !!formErrors?.loginName) && (
                  <FormWarning
                    className="font-sm flex-grow-1 mt-2"
                    message={
                      <FormattedMessage
                        id={`${
                          showEmailDomains
                            ? "MANAGEMENT.ADD_USER.LOGIN_NAME_REQUIRED"
                            : formErrors?.email?.type === "pattern"
                            ? "MANAGEMENT.ADD_USER.EMAIL_PATTERN_REQUIRED"
                            : "MANAGEMENT.ADD_USER.EMAIL_REQUIRED"
                        }`}
                        defaultMessage={`${
                          showEmailDomains
                            ? "Insert Login Name"
                            : formErrors?.email?.type === "pattern"
                            ? "Insert Valid Email"
                            : "Insert Email"
                        }`}
                      />
                    }
                  />
                )}
              </Form.Group>
              {/* {isModal && (
                <Form.Group as={Col} className="pl-3 px-0">
                  <CheckBoxFormInput<UserDetailsFormInputs>
                    name={"sendActivationEmail"}
                    control={control}
                    input={{
                      className: "mt-3",
                      label: (
                        <span className="font-md">
                          <FormattedMessage
                            id="MANAGEMENT.ADD_USER.SEND_ACTIVATION_EMAIL"
                            defaultMessage="Send activation email"
                          />
                        </span>
                      ),
                    }}
                  />
                </Form.Group>
              )}
              </Row>
            <Row> */}
              <Form.Group
                as={Col}
                className={`mb-0 col-${showEmailDomains ? "3" : "6"}`}
                // className={`mb-0 col-${showEmailDomains ? "6" : "12"}`}
              >
                <Form.Label className="mb-1 font-md">
                  <FormattedMessage id="USER.PHONE" defaultMessage="Phone" />{" "}
                  {"("}
                  <FormattedMessage
                    id="USER.PHONE.DIGITS_ONLY"
                    defaultMessage="Digits Only"
                  />
                  {")"}:
                </Form.Label>

                {/* <div style={{ display: "flex" }}>
                  <Controller
                    name="countryCode"
                    control={control}
                    render={({ field }) => (
                      <CountryCodeSelect
                        setValueOnChange={(countryDetails) => {
                          field.onChange(countryDetails.phoneCode);
                          setRequiredPhoneLength(
                            countryDetails.phoneNumberLength
                          );
                        }}
                        defaultPhoneCode={defaultPhoneCode}
                      />
                    )}
                  ></Controller> */}

                <TextFormInput
                  name={"phone"}
                  control={control}
                  rules={{
                    pattern: numberPattern,
                    minLength: 5, //requiredPhoneLength,
                    // maxLength: requiredPhoneLength,
                  }}
                  input={{
                    isInvalid: !!formErrors?.phone,
                    disabled: isSubmitting || loading || disabled,
                    readOnly: isSubmitting || loading || disabled,
                  }}
                />
                {!!formErrors?.phone ? (
                  <FormWarning
                    className="font-sm flex-grow-1 mt-2"
                    message={
                      <FormattedMessage
                        id="MANAGEMENT.ADD_USER.PHONE_PATTERN_REQUIRED"
                        defaultMessage="Insert Digits Only"
                      />
                    }
                  />
                ) : (
                  errorPlaceholder
                )}
                {/* </div> */}
              </Form.Group>
            </Row>
          </div>
        );
      }}
    </ConnectForm>
  );
};
export default UserInfoInputs;
